import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        // Remove Content-Type for FormData so browser sets it with boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== LESSON API FUNCTIONS ====================

/**
 * Get list of all lessons (Authenticated users)
 * @returns {Promise} Array of lessons
 */
export const getAllLessons = async () => {
    try {
        const response = await api.get('/lessons/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get lessons by chapter ID (server-side filtered)
 * @param {number} chapterId - Chapter ID to filter by
 * @returns {Promise} Array of lessons for the chapter
 */
export const getLessonsByChapter = async (chapterId) => {
    try {
        const response = await api.get(`/lessons/?chapter=${chapterId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get lessons for a single level (server-side filtered, spans every chapter in the level)
 * @param {number} levelId - Level ID to filter by
 * @returns {Promise} Array of lessons belonging to the level
 */
export const getLessonsByLevel = async (levelId) => {
    try {
        const response = await api.get(`/lessons/?level=${levelId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new lesson (Admin only)
 * @param {Object} lessonData - Lesson data object
 * @param {string} lessonData.title - Lesson title
 * @param {number} lessonData.chapter - Chapter ID
 * @param {number} lessonData.order - Lesson order
 * @returns {Promise} Created lesson data
 */
export const createLesson = async (lessonData) => {
    try {
        const response = await api.post('/lessons/', lessonData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing lesson (Admin only)
 * @param {number} lessonId - Lesson ID
 * @param {Object} lessonData - Updated lesson data
 * @returns {Promise} Updated lesson data
 */
export const updateLesson = async (lessonId, lessonData) => {
    try {
        const response = await api.patch(`/lessons/${lessonId}/`, lessonData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a lesson (Admin only)
 * @param {number} lessonId - Lesson ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteLesson = async (lessonId) => {
    try {
        const response = await api.delete(`/lessons/${lessonId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== LESSON PAGE API FUNCTIONS ====================

/**
 * Get all lesson pages (optionally filtered by lesson)
 * @param {number} lessonId - Optional lesson ID to filter by
 * @returns {Promise} Array of lesson pages
 */
export const getLessonPages = async (lessonId = null) => {
    try {
        const url = lessonId ? `/lesson-pages/?lesson=${lessonId}` : '/lesson-pages/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get lesson pages for multiple lessons in a single request (avoids one request per lesson)
 * @param {Array<number>} lessonIds - Lesson IDs to fetch pages for
 * @returns {Promise} Array of lesson pages across all the given lessons
 */
export const getLessonPagesByIds = async (lessonIds) => {
    if (!lessonIds || lessonIds.length === 0) return [];
    try {
        const response = await api.get(`/lesson-pages/?lesson_ids=${lessonIds.join(',')}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single lesson page by ID
 * @param {number} pageId - Page ID
 * @returns {Promise} Lesson page data with blocks
 */
export const getLessonPage = async (pageId) => {
    try {
        const response = await api.get(`/lesson-pages/${pageId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new lesson page (Admin only)
 * @param {Object} pageData - Page data object
 * @param {string} pageData.title - Page title
 * @param {number} pageData.lesson - Lesson ID
 * @param {number} pageData.order - Page order
 * @returns {Promise} Created page data
 */
export const createLessonPage = async (pageData) => {
    try {
        const response = await api.post('/lesson-pages/', pageData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a lesson page (Admin only)
 * @param {number} pageId - Page ID
 * @param {Object} pageData - Updated page data
 * @returns {Promise} Updated page data
 */
export const updateLessonPage = async (pageId, pageData) => {
    try {
        const response = await api.patch(`/lesson-pages/${pageId}/`, pageData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a lesson page (Admin only)
 * @param {number} pageId - Page ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteLessonPage = async (pageId) => {
    try {
        const response = await api.delete(`/lesson-pages/${pageId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== LESSON PAGE BLOCK API FUNCTIONS ====================

/**
 * Get all lesson page blocks (optionally filtered by page)
 * @param {number} pageId - Optional page ID to filter by
 * @returns {Promise} Array of blocks
 */
export const getLessonPageBlocks = async (pageId = null) => {
    try {
        const url = pageId ? `/lesson-page-blocks/?page=${pageId}` : '/lesson-page-blocks/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single block by ID
 * @param {number} blockId - Block ID
 * @returns {Promise} Block data
 */
export const getLessonPageBlock = async (blockId) => {
    try {
        const response = await api.get(`/lesson-page-blocks/${blockId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new lesson page block (Admin only)
 * @param {Object} blockData - Block data object
 * @returns {Promise} Created block data
 */
export const createLessonPageBlock = async (blockData) => {
    try {
        const response = await api.post('/lesson-page-blocks/', blockData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a lesson page block (Admin only)
 * @param {number} blockId - Block ID
 * @param {Object} blockData - Updated block data
 * @returns {Promise} Updated block data
 */
export const updateLessonPageBlock = async (blockId, blockData) => {
    try {
        const response = await api.patch(`/lesson-page-blocks/${blockId}/`, blockData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a lesson page block (Admin only)
 * @param {number} blockId - Block ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteLessonPageBlock = async (blockId) => {
    try {
        const response = await api.delete(`/lesson-page-blocks/${blockId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== QUIZ BLOCK API FUNCTIONS ====================

/**
 * Get all quiz blocks
 * @returns {Promise} Array of quiz blocks
 */
export const getQuizBlocks = async () => {
    try {
        const response = await api.get('/quiz-blocks/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single quiz block by ID
 * @param {number} blockId - Block ID
 * @returns {Promise} Quiz block data
 */
export const getQuizBlock = async (blockId) => {
    try {
        const response = await api.get(`/quiz-blocks/${blockId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new quiz block (Admin only)
 * @param {Object} blockData - Block data object
 * @returns {Promise} Created block data
 */
export const createQuizBlock = async (blockData) => {
    try {
        const response = await api.post('/quiz-blocks/', blockData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a quiz block (Admin only)
 * @param {number} blockId - Block ID
 * @param {Object} blockData - Updated block data
 * @returns {Promise} Updated block data
 */
export const updateQuizBlock = async (blockId, blockData) => {
    try {
        const response = await api.patch(`/quiz-blocks/${blockId}/`, blockData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a quiz block (Admin only)
 * @param {number} blockId - Block ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteQuizBlock = async (blockId) => {
    try {
        const response = await api.delete(`/quiz-blocks/${blockId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== QUIZ QUESTION API FUNCTIONS ====================

/**
 * Get all quiz questions (optionally filtered by quiz block)
 * @param {number} quizBlockId - Optional quiz block ID to filter by
 * @returns {Promise} Array of questions
 */
export const getQuizQuestions = async (quizBlockId = null) => {
    try {
        const url = quizBlockId ? `/quiz-questions/?quiz_block=${quizBlockId}` : '/quiz-questions/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get quiz questions belonging to any quiz block in a chapter (server-side filtered)
 * @param {number} chapterId - Chapter ID to filter by
 * @returns {Promise} Array of questions
 */
export const getQuizQuestionsByChapter = async (chapterId) => {
    try {
        const response = await api.get(`/quiz-questions/?chapter=${chapterId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single quiz question by ID
 * @param {number} questionId - Question ID
 * @returns {Promise} Question data
 */
export const getQuizQuestion = async (questionId) => {
    try {
        const response = await api.get(`/quiz-questions/${questionId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new quiz question (Admin only)
 * @param {Object} questionData - Question data object
 * @returns {Promise} Created question data
 */
export const createQuizQuestion = async (questionData) => {
    try {
        const response = await api.post('/quiz-questions/', questionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a quiz question (Admin only)
 * @param {number} questionId - Question ID
 * @param {Object} questionData - Updated question data
 * @returns {Promise} Updated question data
 */
export const updateQuizQuestion = async (questionId, questionData) => {
    try {
        const response = await api.patch(`/quiz-questions/${questionId}/`, questionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a quiz question (Admin only)
 * @param {number} questionId - Question ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteQuizQuestion = async (questionId) => {
    try {
        const response = await api.delete(`/quiz-questions/${questionId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== QUIZ CHOICE API FUNCTIONS ====================

/**
 * Get all quiz choices (optionally filtered by quiz block)
 * @param {number} quizBlockId - Optional quiz block ID to filter by
 * @returns {Promise} Array of choices
 */
export const getQuizChoices = async (quizQuestionId = null) => {
    try {
        const url = quizQuestionId ? `/quiz-choices/?quiz_question=${quizQuestionId}` : '/quiz-choices/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get quiz choices belonging to any quiz question in a chapter (server-side filtered)
 * @param {number} chapterId - Chapter ID to filter by
 * @returns {Promise} Array of choices
 */
export const getQuizChoicesByChapter = async (chapterId) => {
    try {
        const response = await api.get(`/quiz-choices/?chapter=${chapterId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single quiz choice by ID
 * @param {number} choiceId - Choice ID
 * @returns {Promise} Choice data
 */
export const getQuizChoice = async (choiceId) => {
    try {
        const response = await api.get(`/quiz-choices/${choiceId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new quiz choice (Admin only)
 * @param {Object} choiceData - Choice data object
 * @returns {Promise} Created choice data
 */
export const createQuizChoice = async (choiceData) => {
    try {
        const response = await api.post('/quiz-choices/', choiceData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update a quiz choice (Admin only)
 * @param {number} choiceId - Choice ID
 * @param {Object} choiceData - Updated choice data
 * @returns {Promise} Updated choice data
 */
export const updateQuizChoice = async (choiceId, choiceData) => {
    try {
        const response = await api.patch(`/quiz-choices/${choiceId}/`, choiceData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a quiz choice (Admin only)
 * @param {number} choiceId - Choice ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteQuizChoice = async (choiceId) => {
    try {
        const response = await api.delete(`/quiz-choices/${choiceId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Normalize the media URL for a lesson page block.
 * Prefers the API-provided media_url, then URL_media, then legacy value fields.
 */
export const getLessonPageBlockMediaUrl = (block) => {
    if (!block) return null;
    return block.media_url || block.URL_media || block.media_file || block.value || null;
};

// ==================== USER QUIZ ATTEMPT API FUNCTIONS ====================

/**
 * Get user quiz attempts (optionally filtered by quiz_block)
 * @param {number} quizBlockId - Optional quiz block ID to filter by
 * @returns {Promise} Array of attempts
 */
export const getUserQuizAttempts = async (quizBlockId = null) => {
    try {
        const url = quizBlockId ? `/user-quiz-attempts/?quiz_block=${quizBlockId}` : '/user-quiz-attempts/';
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get user quiz attempts scoped to one chapter (server-side filtered). For staff this narrows
 * the "every user's attempts" branch down to just this chapter's quiz blocks; for non-staff it's
 * already scoped to the current user regardless.
 * @param {number} chapterId - Chapter ID to filter by
 * @returns {Promise} Array of attempts
 */
export const getUserQuizAttemptsByChapter = async (chapterId) => {
    try {
        const response = await api.get(`/user-quiz-attempts/?chapter=${chapterId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a user quiz attempt (or update if server finds existing)
 * @param {Object} attemptData - { quiz_block, completed, passed }
 * @returns {Promise} Created/updated attempt
 */
export const createUserQuizAttempt = async (attemptData) => {
    try {
        const response = await api.post('/user-quiz-attempts/', attemptData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing user quiz attempt by id
 * @param {number} attemptId
 * @param {Object} attemptData
 */
export const updateUserQuizAttempt = async (attemptId, attemptData) => {
    try {
        const response = await api.patch(`/user-quiz-attempts/${attemptId}/`, attemptData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export default api;
